import { defineStore } from "pinia";
import type { DateRange } from "@/types/date";
import date from "@/libs/date.ts";
import type { Dayjs } from "dayjs";

export const useDateRange = defineStore("DateRange", {
  state: (): { [key: string]: Dayjs } => ({
    from: date().startOf("month"),
    to: date().add(5, "months").endOf("month"),
    week: date().startOf("isoWeek"),
  }),

  getters: {
    months(state) {
      const months = [];

      let current = state.from;

      while (current.isBefore(state.to)) {
        months.push(current);
        current = current.add(1, "month");
      }

      return months;
    },

    // Every week of the viewed week's year, for the week number dropdown. Week 1 starts on the
    // Monday of the week holding 4 January, so the numbers are the familiar ISO ones (52 or 53).
    weeks(state): Dayjs[] {
      const year = state.week.isoWeekYear();

      const weeks = [];

      let current = date().year(year).month(0).date(4).startOf("isoWeek");

      while (current.isoWeekYear() === year) {
        weeks.push(current);
        current = current.add(7, "day");
      }

      return weeks;
    },

    days(state): Dayjs[] {
      return Array.from({ length: 7 }, (_, day) => state.week.add(day, "day"));
    },

    range(state): DateRange {
      return {
        from: date(state.from),
        to: date(state.to),
      };
    },

    // The window snapped to whole weeks: the week view can then show a Mon..Sun that sticks
    // out of the month window on either side without losing the data for those days.
    weekRange(state): DateRange {
      return {
        from: date(state.from.startOf("isoWeek")),
        to: date(state.to.endOf("isoWeek")),
      };
    },
  },

  actions: {
    // Dayjs is immutable: discarding the result of `add`/`subtract` left these as no-ops.
    nextMonth() {
      this.from = this.from.add(1, "month");
    },
    previousMonth() {
      this.from = this.from.subtract(1, "month");
    },
    nextYear() {
      this.from = this.from.add(1, "year");
    },
    previousYear() {
      this.from = this.from.subtract(1, "year");
    },
    selectFrom(date: Dayjs) {
      this.from = date;
    },
    selectTo(date: Dayjs) {
      this.to = date;
    },

    nextWeek() {
      this.week = this.week.add(7, "day");
      this.focusWeek();
    },

    previousWeek() {
      this.week = this.week.subtract(7, "day");
      this.focusWeek();
    },

    currentWeek() {
      this.selectWeek(date().startOf("isoWeek"));
    },

    // Jump straight to a week picked from the dropdown; `week` is always a Monday.
    selectWeek(week: Dayjs) {
      this.week = week.startOf("isoWeek");
      this.focusWeek();
    },

    // The week view drives the window: when the viewed week no longer fits in it, reload from
    // that week's month onwards, keeping the same six month span.
    focusWeek() {
      const start = this.week.startOf("isoWeek");
      const end = this.week.endOf("isoWeek");

      if (start.isBefore(this.from.startOf("isoWeek")) || end.isAfter(this.to.endOf("isoWeek"))) {
        const month = this.week.startOf("month");

        this.from = month;
        this.to = month.add(5, "months").endOf("month");
      }
    },

    // A manual month pick can leave the viewed week outside the window; snap it to the
    // window's first/last week so the next arrow click walks normally again.
    clampWeek() {
      if (this.week.startOf("month").isBefore(this.from.startOf("month"))) {
        this.week = this.from.startOf("isoWeek");
      } else if (this.week.startOf("month").isAfter(this.to.startOf("month"))) {
        this.week = this.to.startOf("isoWeek");
      }
    },
  },
});
